/**
 * **Two pinned occurrences, side by side.** Canon:
 * `docs/design/ankiety-klienta-2026-09-16/03-modal-porownania.html`.
 *
 * ── It projects the matrix rather than re-reading the surveys ────────────────────────────────
 * Every fact the comparison needs — the rows in form order, the ten measurement sub-rows, the
 * photos, the clips, what each field's direction MEANS — is already decided by `buildSurveyHistory`
 * for the matrix behind the dialog. Reading the source a second time would be a second answer to
 * the same question, and the two would drift the first time a row gained a rule. So this takes the
 * built view and picks two columns out of it.
 *
 * ── The change is B minus A, and "brak" is not zero ──────────────────────────────────────────
 * A row one side did not answer has no change. Printing `0` there would say the client's waist did
 * not move across three weeks in which nobody measured it, which is the defect this module's own
 * test is named after.
 *
 * ── The row is kept even when NEITHER side answered ──────────────────────────────────────────
 * The dialog compares two specific occurrences, and "you asked this and got nothing twice" is an
 * answer a trainer wants. Only a row ABSENT from both snapshots comes out — nobody was asked, so
 * there is nothing to report.
 *
 * Pure: no React, no DOM, no query hook, no i18next import.
 */

import { changeWithUnit, deltaTone, roundedDelta, signed } from '~/features/survey-report';
import type {
  SurveyCellView,
  SurveyColumnView,
  SurveyDeltaTone,
  SurveyHistoryView,
  SurveyRowView,
} from '~/features/survey-report';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface SurveyCompareRowView {
  readonly key: string;
  /**
   * The question's SHORT name, exactly as the report's own reading rows name it — „Waga", „Sen".
   * A question with no catalog code has no short form and keeps the trainer's own wording, which is
   * what `shortLabel` falls back to. One vocabulary across the two surfaces: the same question was
   * „Ile dziś ważysz?" in the table and „Waga" in the report a press away.
   */
  readonly label: string;
  readonly kind: SurveyRowView['kind'];
  readonly sub: boolean;
  readonly left: SurveyCellView;
  readonly right: SurveyCellView;
  /**
   * Whether a change between the two sides is a thing that EXISTS on this row.
   *
   * A photograph, a clip and a paragraph of text have no arithmetic between them, so their change
   * cell is blank. „brak" is a different statement — it means the row could have carried a change
   * and one of the two sides never answered — and printing it under a pair of photographs claims a
   * missing measurement that was never asked for.
   */
  readonly comparable: boolean;
  /** Signed change from left to right, rounded as it is printed. `null` when one side is silent. */
  readonly change: number | null;
  /** Signed change from left to right, localised with its unit. `null` when one side is silent. */
  readonly changeText: string | null;
  readonly changeTone: SurveyDeltaTone | null;
}

export interface SurveyCompareView {
  readonly left: SurveyColumnView;
  readonly right: SurveyColumnView;
  readonly rows: readonly SurveyCompareRowView[];
  /**
   * „10 wartości · 7 zmieniło się · talia i biodra w dół, ramię w górę" — the measurement group's
   * own line in this dialog, and the canon's own sentence.
   *
   * The count alone says something moved and leaves the trainer to read ten rows to find out what.
   * Naming the two biggest movers in each direction is the reading they were going to make anyway.
   */
  readonly measurementSummary: string | null;
}

/** The kinds a change between two occurrences is arithmetic on. Everything else has none. */
const COMPARABLE_KINDS: ReadonlySet<SurveyRowView['kind']> = new Set(['number', 'scale', 'measurement']);

/** At most this many field names per direction. Three is a list; two is a sentence. */
const NAMED_PER_DIRECTION = 2;

/** A row whose two sides both answered and whose reading actually changed. */
function hasMoved(row: SurveyCompareRowView): boolean {
  return row.change !== null && row.change !== 0;
}

/**
 * „talia i biodra w dół, ramię w górę" — the biggest movers, by name, in each direction.
 *
 * The two largest magnitudes each way, because a sentence that names all ten is the table again.
 * `Intl.ListFormat` does the joining rather than a „ i " in the locale file: the conjunction and
 * the comma before it are grammar, and every language this app ships has its own.
 */
function namedDirections(
  measurementRows: readonly SurveyCompareRowView[],
  locale: string,
  t: Translate,
): string | null {
  const moved = measurementRows.filter(hasMoved);
  const biggest = (sign: number): readonly string[] =>
    moved
      .filter((row) => Math.sign(row.change ?? 0) === sign)
      .sort((left, right) => Math.abs(right.change ?? 0) - Math.abs(left.change ?? 0))
      .slice(0, NAMED_PER_DIRECTION)
      .map((row) => row.label.toLocaleLowerCase(locale));

  const list = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' });
  const down = biggest(-1);
  const up = biggest(1);
  const clauses = [
    ...(down.length === 0
      ? []
      : [t('web:clientDetail.surveys.compareMeasureDown', { list: list.format(down) })]),
    ...(up.length === 0
      ? []
      : [t('web:clientDetail.surveys.compareMeasureUp', { list: list.format(up) })]),
  ];
  return clauses.length === 0 ? null : clauses.join(', ');
}

export interface SurveyCompareDeps {
  readonly t: Translate;
  readonly locale: string;
}

/**
 * @param leftId the occurrence on the left. `swapped` is the caller's business — it passes the two
 *   ids the other way round, so there is no second code path for the „Zamień strony" button.
 */
export function buildSurveyCompare(
  history: SurveyHistoryView,
  leftId: number,
  rightId: number,
  deps: SurveyCompareDeps,
): SurveyCompareView | null {
  const { t, locale } = deps;
  const leftIndex = history.columns.findIndex((column) => column.id === leftId);
  const rightIndex = history.columns.findIndex((column) => column.id === rightId);
  const left = history.columns[leftIndex];
  const right = history.columns[rightIndex];
  // A pin that fell out of the visible range — the trainer moved the range bar with two columns
  // pinned — has no column to compare. The dialog closes rather than drawing half a table.
  if (left === undefined || right === undefined) return null;

  const rows: SurveyCompareRowView[] = history.rows.flatMap((row) => {
    const leftCell = row.cells[leftIndex];
    const rightCell = row.cells[rightIndex];
    if (leftCell === undefined || rightCell === undefined) return [];
    if (leftCell.state === 'absent' && rightCell.state === 'absent') return [];

    const comparable = COMPARABLE_KINDS.has(row.kind);
    const a = leftCell.state === 'value' ? leftCell.number : null;
    const b = rightCell.state === 'value' ? rightCell.number : null;
    // Rounded before it is printed OR judged — see `roundedDelta`.
    const change = !comparable || a === null || b === null ? null : roundedDelta(b - a);

    return [
      {
        key: row.key,
        label: row.shortLabel,
        kind: row.kind,
        sub: row.sub,
        left: leftCell,
        right: rightCell,
        comparable,
        change,
        // The words and the sign come from `signed` — the matrix's own, so a change reads the same
        // in the comparison as it does in the cell it was computed from. The unit is the ROW's,
        // which every row was minted with: reading it off whichever cell happened to carry one
        // meant a row where neither side answered had no unit to print at all.
        // A change of zero says so in words — see `changeWithUnit`.
        changeText:
          change === null ? null : changeWithUnit(t, change, signed(change, locale), row.unit),
        // No reading on one side means no verdict at all. A change on a row with no agreed
        // direction — a rating, a weight — is printed `flat`: the figure stands, uncoloured.
        changeTone:
          change === null ? null : row.lowGood === null ? 'flat' : deltaTone(change, row.lowGood),
      },
    ];
  });

  const measurementRows = rows.filter((row) => row.kind === 'measurement');
  // The COUNT is of directed movers only, which is not quite `hasMoved` above: the composite's
  // weight sub-row moves like any other and carries no agreed direction, so it is named among the
  // movers and left out of „7 zmieniło się". Deliberately two predicates, because the sentence and
  // the count are answering two questions.
  const moved = measurementRows.filter(
    (row) => hasMoved(row) && row.changeTone !== 'flat',
  ).length;
  const directions = namedDirections(measurementRows, locale, t);
  // ONE key rather than a sentence wrapped in a second one: both forms are a whole sentence a
  // translator reads end to end, and the plural agreement of „zmieniło się" belongs beside the
  // count it agrees with.
  const summary =
    measurementRows.length === 0
      ? null
      : t(
          directions === null
            ? 'web:clientDetail.surveys.compareMeasureSummary'
            : 'web:clientDetail.surveys.compareMeasureSummaryNamed',
          { total: measurementRows.length, count: moved, directions: directions ?? '' },
        );

  return {
    left,
    right,
    measurementSummary: summary,
    rows,
  };
}
